import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import actions from "@/actions";
import FileItem from "@/components/FileItem";
import { useTimestamp } from "@/hooks";
import { UploadFileMode } from "@/models/files";
import { RootState } from "@/reducers/types";
import { TooltipItem } from "@/sx-layout/components/plotmap/components/TooltipItem";
import { Machine, MachineStatusLabel } from "@/sx-layout/components/plotmap/models";
import { MachineType } from "@/sx-layout/components/settings/machineSetting/models";

type Props = {
  data: Machine;
  leftOrRight: "left-aligned" | "right-aligned";
};

export const TooltipMachine: FC<Props> = ({ data, leftOrRight }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const machineTypes = useSelector<RootState, MachineType[]>((state) => state.machine.machineTypes);
  const hasAttachedFile = data.files && data.files.length;
  const { formattedInsertDatetime, insertUserNameWithCompanyName } = useTimestamp(data.timestamp);

  const renderFileList = () => {
    return (
      <div className="file-upload-box w-[100%]">
        <ul className="form-file-list">
          {data.files
            .filter((f) => f.mode !== UploadFileMode.DELETE)
            .map((file, index) => (
              <FileItem
                // @ts-ignore
                additionalFilenameClass="text-[1rem] !pl-0 mw-90"
                additionalImageAreaClass="!text-left"
                key={index}
                // @ts-ignore
                kind="machines"
                file={file}
                onDownload={(kind, fileId, callback) =>
                  dispatch(actions.common.downloadAttachmentFile(kind, fileId, callback))
                }
              />
            ))}
        </ul>
      </div>
    );
  };

  return (
    <div
      style={
        leftOrRight === "left-aligned"
          ? {
              left: "15px",
            }
          : {
              right: "15px",
            }
      }
      className={`
        sx-layout-tooltip
        border-4
        border-[#dea321]
        border-solid
        text-[13px]
        shadow-[0_4px_4px_0px_rgba(0,0,0,0.3)]
        rounded-lg
        p-3
        bg-[#fff]
        z-10
      `}
      id="tooltip-machine"
    >
      <img
        style={{ position: "absolute", top: "10px", right: "10px", width: "20px", height: "20px" }}
        src="./img/tooltip_machine.svg"
      />
      {data.keepout_area_id && (
        <img
          style={{ position: "absolute", top: "30px", right: "10px", width: "20px", height: "20px" }}
          src="./img/icon-link.svg"
        />
      )}
      <TooltipItem title="No" value={data.no?.toString()} />
      <TooltipItem title={t("heavy_machine")} value={data.machine_type_name} />
      <TooltipItem title={t("size")} value={`${Math.round(data.size * 100)}%`} />
      <TooltipItem title={t("radio")} value={data.wireless_flg === true ? t("use") : t("negation")} />
      <TooltipItem title={t("jib_extension")} value={data.jib_flg === true ? t("extension") : t("not_extension")} />
      <TooltipItem
        title={t("substitute")}
        value={
          machineTypes.find((machineType: MachineType) => machineType.machine_type_id === data.substitute)
            ?.machine_type_name ?? t("disuse")
        }
      />
      <TooltipItem title={t("operation_time")} value={`${data.use_start_hour}~${data.use_end_hour}`} />
      <TooltipItem title={t("content")} value={data.work_content} />
      <TooltipItem title={t("belonging")} value={data.company_name} />
      <TooltipItem title={t("machine_user")} value={data.charge_user_name} />
      <TooltipItem title={t("contact")} value={data.phone_number} />
      <TooltipItem title={t("machine_status")} value={t(MachineStatusLabel[data.status])} />
      <TooltipItem title={t("note")} value={data.note} />
      {hasAttachedFile ? (
        <div className="flex pt-[10px] flex-col">
          <span className="w-[80px] text-right font-bold mr-2 text-[#888888]">{t("attached_file")}</span>
          <span className="text-left mt-4">{renderFileList()}</span>
        </div>
      ) : (
        <div className="flex pt-[10px]">
          <span className="w-[80px] text-right font-bold mr-2 text-[#888888]">{t("attached_file")}</span>
          <span className="text-left">{data.file_num ? `${data.file_num}${t("unit")}` : t("no_attached_file")}</span>
        </div>
      )}
      <br />
      <TooltipItem metaData={true} title={t("created_at")} value={formattedInsertDatetime} />
      <TooltipItem metaData={true} title={t("registered_person")} value={insertUserNameWithCompanyName} />
    </div>
  );
};
